import { useState, useEffect, useCallback } from 'react';
import { getConversationMessages, sendMessage, markMessagesRead } from '../utils/velvypalApi';
import { logger } from '../utils/logger';

/**
 * Hook for managing a single velvypal conversation
 * @param {string|null} connectionId - Connection ID to fetch messages for
 * @param {Object|null} user - Firebase user object
 * @returns {{
 *   messages: Array,
 *   connection: Object|null,
 *   loading: boolean,
 *   sending: boolean,
 *   error: string|null,
 *   send: Function,
 *   refresh: Function,
 *   markRead: Function
 * }}
 */
export function useConversation(connectionId, user) {
  const [messages, setMessages] = useState([]);
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const fetchMessages = useCallback(async () => {
    if (!connectionId || !user) {
      setMessages([]);
      setConnection(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getConversationMessages(user, connectionId);
      setMessages(data.messages || []);
      setConnection(data.connection || null);
    } catch (err) {
      logger.error('Failed to fetch messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [connectionId, user]);

  // Fetch on mount and when connectionId changes
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Send a message
  const send = useCallback(async (content) => {
    if (!connectionId || !user || !content.trim()) {
      return { success: false, error: 'Invalid input' };
    }

    setSending(true);
    setError(null);

    try {
      const result = await sendMessage(user, connectionId, content);
      if (result.success) {
        // Refresh messages after sending
        await fetchMessages();
      }
      return result;
    } catch (err) {
      logger.error('Failed to send message:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setSending(false);
    }
  }, [connectionId, user, fetchMessages]);

  // Mark messages as read
  const markRead = useCallback(async () => {
    if (!connectionId || !user) return;

    try {
      await markMessagesRead(user, connectionId);
    } catch (err) {
      logger.error('Failed to mark messages read:', err);
    }
  }, [connectionId, user]);

  // Refresh function for external use
  const refresh = useCallback(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    messages,
    connection,
    loading,
    sending,
    error,
    send,
    refresh,
    markRead,
  };
}
